/**
 * Who is asking, and may they.
 *
 * Three questions, answered in order, each one a gate the next depends on:
 *
 *  1. **Signed in?** Clerk's own hosted sign-in, mounted in place. No route of
 *     our own, so there is nothing to keep in step with Clerk's flows.
 *  2. **May this account use this tenant?** `POST /auth/tenant-access`. Each
 *     tenant is its own deployment against its own database, and the Clerk
 *     instance is shared across all of them — being signed in says nothing
 *     about belonging here.
 *  3. **Is the right organisation active?** The answer to (2) names the org;
 *     Clerk's active org is what the backend reads off the token, so a mismatch
 *     is corrected rather than reported.
 *
 * All three sit above the router, which is what lets every feature component
 * assume a signed-in user in an active org and carry no branch for the absence
 * of one.
 */
import { ClerkProvider, SignIn, useAuth, useOrganizationList, useUser } from '@clerk/clerk-react'
import { useEffect, useState, type ReactNode } from 'react'
import { setCurrentPerson } from '@/data/people'
import { apiSend, setCurrentUserId, setIsOrgAdmin, setTokenGetter } from '@/lib/api'

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined

/**
 * Local development signs in through the dev server, not through Clerk's
 * browser SDK — production keys refuse localhost outright. The token is still a
 * real Clerk token; see `devAuthBridge` in `vite.config.ts` for why this is not
 * an auth bypass. `import.meta.env.DEV` is a build-time constant, so the whole
 * branch is dropped from a production bundle along with everything it names.
 */
const DEV_AUTH = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === '1'

/** Exposed so a surface that needs real Clerk can say so instead of crashing. */
export const IS_DEV_AUTH = DEV_AUTH

/** What the dev bridge answers with, and what the dev hooks below read. */
interface DevIdentity {
  userId: string
  email: string
  name: string
  orgRole: string
}

let devIdentity: DevIdentity | null = null

/** What `POST /auth/tenant-access` answers. */
interface TenantAccess {
  allowed: boolean
  orgId?: string | null
  email?: string | null
  message?: string | null
}

/**
 * Clerk's screens, in this app's clothes.
 *
 * `<SignIn>` and `<UserProfile>` are Clerk's own components rendered inside our
 * chrome, and untouched they arrive with Clerk's radii, its blue, and its
 * shadows — which reads as a third-party widget bolted into the page. The
 * variables below are the design system's tokens, so the two agree without
 * anyone maintaining a copy of Clerk's stylesheet.
 *
 * Tokens are resolved at call time rather than hard-coded: they are CSS
 * variables, and reading them keeps a future change to the palette in one file.
 */
function clerkAppearance() {
  const token = (name: string, fallback: string) =>
    typeof window === 'undefined'
      ? fallback
      : getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback

  return {
    variables: {
      colorPrimary: token('--color-fg', '#101215'),
      colorText: token('--color-fg', '#101215'),
      colorTextSecondary: token('--color-fg-muted', '#5b6470'),
      colorBackground: token('--color-surface', '#ffffff'),
      colorInputBackground: token('--color-surface', '#ffffff'),
      colorInputText: token('--color-fg', '#101215'),
      colorDanger: token('--color-danger', '#b42318'),
      borderRadius: token('--radius-sm', '6px'),
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '13px',
    },
    elements: {
      // The card sits inside our own frame, which already draws the border.
      cardBox: { boxShadow: 'none', border: `1px solid ${token('--color-border', '#e4e7ec')}` },
      card: { boxShadow: 'none' },
      footer: { background: 'transparent' },
    },
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (DEV_AUTH) return <DevAuthProvider>{children}</DevAuthProvider>
  if (!PUBLISHABLE_KEY) {
    return <Fatal title="Not configured" detail="VITE_CLERK_PUBLISHABLE_KEY is unset." />
  }
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      afterSignOutUrl="/"
      appearance={clerkAppearance()}
    >
      <TokenBridge />
      {children}
    </ClerkProvider>
  )
}

/**
 * The dev-server equivalent of `ClerkProvider` + `TokenBridge`.
 *
 * Fetches once to learn who it is, and points the API client at the same
 * endpoint for every subsequent token — Clerk's session tokens expire after 60
 * seconds, so a token cached for the lifetime of the tab would fail almost
 * immediately.
 */
function DevAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'loading' | 'ready' | string>('loading')

  useEffect(() => {
    setTokenGetter(async () => {
      const response = await fetch('/__dev/token')
      if (!response.ok) return null
      const identity = (await response.json()) as DevIdentity & { jwt: string }
      devIdentity = identity
      setCurrentUserId(identity.userId)
      return identity.jwt
    })

    void (async () => {
      const response = await fetch('/__dev/token')
      const body = (await response.json()) as (DevIdentity & { jwt: string }) | { error: string }
      if (!response.ok || 'error' in body) {
        setState('error' in body ? body.error : 'Could not mint a development token.')
        return
      }
      devIdentity = body
      setCurrentUserId(body.userId)
      setIsOrgAdmin(body.orgRole === 'org:admin')
      setCurrentPerson({ email: body.email, name: body.name, isInternal: true })
      setState('ready')
    })()
  }, [])

  if (state === 'loading') return <Waiting label="Signing in…" />
  if (state !== 'ready') return <Fatal title="Development sign-in failed" detail={state} />
  return <>{children}</>
}

/**
 * Hands Clerk's `getToken` to the API client.
 *
 * Renders nothing and lives directly under the provider so it runs before any
 * gate below it can issue a request — `src/data/` is plain async functions, not
 * hooks, so this is the only way the token reaches them.
 */
function TokenBridge() {
  const { getToken, isLoaded, userId, orgRole } = useAuth()
  const { user } = useUser()
  useEffect(() => {
    if (!isLoaded) return
    setTokenGetter(() => getToken())
    setCurrentUserId(userId ?? null)
    setIsOrgAdmin(orgRole === 'org:admin' || orgRole === 'org:super_admin')
  }, [isLoaded, getToken, userId, orgRole])
  useEffect(() => {
    if (!user) return
    const email = user.primaryEmailAddress?.emailAddress ?? ''
    setCurrentPerson({
      email,
      name: user.fullName ?? email,
      isInternal: true,
      imageUrl: user.hasImage ? user.imageUrl : null,
    })
  }, [user])
  return null
}

/**
 * The gate. Children render only for a signed-in account that belongs to this
 * tenant, with that tenant's organisation active.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  // Under the dev bridge the credential is minted, not signed in for, and the
  // organisation is whichever one the borrowed session already had active —
  // there is nothing here left to gate.
  if (DEV_AUTH) return <>{children}</>
  return <ClerkSession>{children}</ClerkSession>
}

function ClerkSession({ children }: { children: ReactNode }) {
  const { isLoaded: authLoaded, isSignedIn, orgId } = useAuth()
  const { isLoaded: orgsLoaded, setActive } = useOrganizationList()
  const [denied, setDenied] = useState<TenantAccess | null>(null)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    if (!authLoaded || !orgsLoaded || !isSignedIn) return
    let cancelled = false

    void (async () => {
      try {
        const access = await apiSend<TenantAccess>('POST', '/auth/tenant-access')
        if (cancelled) return

        if (!access.allowed) {
          setDenied(access)
          setChecked(true)
          return
        }

        // Correcting the active org re-runs this effect with the new `orgId`,
        // which is why `checked` is not set on this branch — the pass that
        // matters is the one after the switch.
        if (access.orgId && access.orgId !== orgId && setActive) {
          await setActive({ organization: access.orgId })
          return
        }

        setDenied(null)
        setChecked(true)
      } catch {
        if (cancelled) return
        setDenied({ allowed: false, message: 'Could not verify access to this workspace.' })
        setChecked(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [authLoaded, orgsLoaded, isSignedIn, orgId, setActive])

  // Sign-in is decided on `authLoaded` ALONE. `useOrganizationList` does not
  // resolve for a signed-out visitor — there is no list to load — so waiting on
  // it before showing the form left every first-time arrival on "Loading…"
  // forever. The org check below still waits for it, because by then there is
  // a session for it to describe.
  if (!authLoaded) return <Waiting />

  if (!isSignedIn) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] p-6">
        <SignIn routing="virtual" />
      </div>
    )
  }

  if (!orgsLoaded || !checked) return <Waiting label="Checking workspace access…" />

  if (denied) {
    return (
      <Fatal
        title="No workspace access"
        detail={denied.message ?? 'You do not have access to this workspace.'}
        note={denied.email ? `Signed in as ${denied.email}` : undefined}
      />
    )
  }

  return <>{children}</>
}

/**
 * Whether the signed-in account administers the organisation.
 *
 * Settings and the template library are admin surfaces in the old app and stay
 * that way; the check is the same two roles the backend enforces, so a
 * non-admin is shown the state rather than a request that will 403.
 */
function useClerkIsOrgAdmin(): boolean {
  const { orgRole } = useAuth()
  return orgRole === 'org:admin' || orgRole === 'org:super_admin'
}

function useDevIsOrgAdmin(): boolean {
  return devIdentity?.orgRole === 'org:admin'
}

/**
 * Which implementation is chosen at module scope, not per render.
 *
 * `DEV_AUTH` is a build-time constant, so a bundle contains exactly one of
 * these and the hook it exports has a stable identity — branching inside the
 * hook would be a conditional `useAuth` call against a provider that is not
 * mounted in development.
 */
export const useIsOrgAdmin = DEV_AUTH ? useDevIsOrgAdmin : useClerkIsOrgAdmin

/** The signed-in person, in the shape `src/data/` uses everywhere else. */
function useClerkCurrentPerson(): { email: string; name: string; isInternal: true } {
  const { user } = useUser()
  return {
    email: user?.primaryEmailAddress?.emailAddress ?? '',
    name: user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? '',
    isInternal: true,
  }
}

function useDevCurrentPerson(): { email: string; name: string; isInternal: true } {
  return {
    email: devIdentity?.email ?? '',
    name: devIdentity?.name ?? devIdentity?.email ?? '',
    isInternal: true,
  }
}

export const useCurrentPerson = DEV_AUTH ? useDevCurrentPerson : useClerkCurrentPerson

/**
 * End the session.
 *
 * Called from the account menu rather than through a Clerk component, so it
 * goes via the global instance the provider already mounted. Under the dev
 * bridge there is no Clerk and no session to end — the credential is minted per
 * request — so it reloads, which is the closest honest equivalent.
 */
export async function signOut(): Promise<void> {
  if (DEV_AUTH) {
    window.location.reload()
    return
  }
  const clerk = (window as unknown as { Clerk?: { signOut: () => Promise<void> } }).Clerk
  await clerk?.signOut()
  window.location.href = '/'
}

function Waiting({ label }: { label?: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)]">
      <span className="text-[13px] text-[var(--color-fg-muted)]">{label ?? 'Loading…'}</span>
    </div>
  )
}

function Fatal({ title, detail, note }: { title: string; detail: string; note?: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] px-6">
      <div className="max-w-md text-center">
        <h1 className="text-[15px] font-semibold text-[var(--color-fg)]">{title}</h1>
        <p className="mt-2 text-[13px] text-[var(--color-fg-muted)]">{detail}</p>
        {note && <p className="mt-2 text-[12px] text-[var(--color-fg-subtle)]">{note}</p>}
      </div>
    </div>
  )
}
